/* @flow */

import React, { useEffect, useRef } from "react";
import cn from "classnames";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import { useData } from "crustate/react";
import { useTranslate } from "@awardit/react-use-translate";
import { Link } from "react-router-dom";
import { QuoteData } from "state/data";
import { Wrapper, Button } from "@crossroads/ui-components";
import useFormat from "helpers/hooks/use-format";
import { useUi, MODE } from "helpers/ui";

import CloseIcon from "icons/close.svg";
import ChevronIcon from "icons/chevron.svg";
import SearchIcon from "icons/search.svg";

import Logo from "components/Logo";

import styles from "./styles.scss";

const Header = (): React$Node => {
  const { closeHamburger: close } = useUi();
  const t = useTranslate();

  return (
    <Wrapper className={styles.header}>
      <Logo />
      <Button aria-label={t("HEADER.CLOSE_MENU")} onClick={() => close(styles.animationDuration)}>
        <CloseIcon aria-label={t("ICONS.CROSS")} className={styles.close} />
      </Button>
    </Wrapper>
  );
};

const TopMenu = (): React$Node => {
  const t = useTranslate();
  const {
    setModeAndOpenMenu: setMode,
    closeHamburger: close,
    viewMode,
    subNavOpen,
  } = useUi();

  return (
    <div className={styles.topMenu}>
      <Link
        className={styles.item}
        to="/all-products"
        onClick={() => {
          close(styles.animationDuration);
        }}
      >
        {t("ALL_PRODUCTS.TITLE")}
      </Link>

      <Button
        aria-expanded={(viewMode === "categories" && subNavOpen) ?? "true"}
        aria-controls="categoriesMobile"
        className={styles.item}
        onClick={() => setMode(MODE.CATEGORIES)}
      >
        <span>{t("HEADER.CATEGORIES")}</span>
        <ChevronIcon aria-label={t("ICONS.CHEVRON")} className={styles.chevron} />
      </Button>

      <Button
        aria-expanded={(viewMode === "brands" && subNavOpen) ?? "true"}
        aria-controls="brandsMobile"
        className={styles.item}
        onClick={() => setMode(MODE.BRANDS)}
      >
        <span>{t("HEADER.BRANDS")}</span>
        <ChevronIcon aria-label={t("ICONS.CHEVRON")} className={styles.chevron} />
      </Button>

      <Link className={styles.item} to={t("CUSTOMER_SERVICE.LINK")}>
        <span>{t("CUSTOMER_SERVICE.TEXT")}</span>
      </Link>

      <Button
        aria-expanded={viewMode === MODE.SEARCH ?? "true"}
        aria-controls="searchForm"
        className={styles.item}
        onClick={() => setMode(MODE.SEARCH)}
      >
        <span>
          <SearchIcon aria-label={t("ICONS.SEARCH")} />
          <span>{t("HEADER.SEARCH")}</span>
        </span>
      </Button>

      <CartButton />
    </div>
  );
};

const CartButton = (): React$Node => {
  const t = useTranslate();
  const { formatPrice } = useFormat();
  const quote = useData(QuoteData).data || null;
  const cartCount = quote?.items.reduce((a, { qty }) => a + qty, 0) || 0;

  return (
    <Link
      className={cn(styles.item, { [styles.linkDisabled]: cartCount === 0 })}
      aria-label={t("HEADER.CART")}
      to={t("URL.CHECKOUT_CART")}
    >
      <span>{t("HEADER.CART")}</span>
      <span>
        <span className={styles.grandTotal}>
          {formatPrice(quote?.grandTotal.incVat || 0)}
        </span>
        <span className={styles.cartCount}>&nbsp;({cartCount})</span>
      </span>
    </Link>
  );
};

const HamburgerMenu = ({ children }: { children: React$Node }): React$Node => {
  const {
    hamburgerOpen,
    subNavOpen,
  } = useUi();

  const ref = useRef();

  useEffect(() => {
    const element = ref.current;

    if (hamburgerOpen && !subNavOpen) {
      if (element) {
        // Select the first focusable element inside the drawer
        const focusable = element.querySelector(
          "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
        );

        if (focusable instanceof HTMLElement) {
          focusable.focus();
        }
      }

      disableBodyScroll(element);
    }
    else {
      enableBodyScroll(element);
    }

    return () => {
      if (element) {
        enableBodyScroll(element);
      }
    };
  }, [subNavOpen, hamburgerOpen]);

  return (
    <div
      id="hamburgerMenu"
      className={cn(
        styles.hamburger,
        { [styles.open]: hamburgerOpen },
        { [styles.subNavOpen]: subNavOpen }
      )}
    >
      <div ref={ref} className={styles.hamburgerMenu}>
        <Header />

        <TopMenu />
      </div>

      {children}
    </div>
  );
};

export default HamburgerMenu;
